// Copyright ©️ 2026 eVolve MEP, LLC

import { Badge, Divider, Group, Text } from '@mantine/core';

import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { convertSecondsToDuration } from 'components/Mantine/durationHelpers';
import { isNil, isNotNil } from 'helpers/isNotNil';

type Props = {
  /** The estimated time for the task in seconds. */
  estimatedTime: number | null | undefined;
  /**
   * The time recorded against the task in seconds,
   * defined as first using the adjusted time, then the logged time, and finally the run time.
   *
   */
  taskTime: number;
  /** Indicates if the task has ever been started. Used to determine if the task time should be displayed. */
  isStarted: boolean;
};

/**
 * Displays the estimated time and the task time for a task or an item.
 * When the item has been started, both times share one badge, split by a vertical divider.
 */
export const TaskTimeBadge = ({ estimatedTime, taskTime, isStarted }: Props) => {
  // If there is no estimated time and the task hasn't been started, don't render anything.
  if (isNil(estimatedTime) && !isStarted) return null;

  const isOverEstimate = isNotNil(estimatedTime) && estimatedTime > 0 && taskTime > estimatedTime;

  return (
    <Badge variant="default" size="xl">
      <Group gap="xs" wrap="nowrap">
        {isNotNil(estimatedTime) && (
          <AgGridStyleTooltip label="Estimated Task Time">
            <Text span inherit c="dimmed">
              {convertSecondsToDuration(estimatedTime)}
            </Text>
          </AgGridStyleTooltip>
        )}
        {isStarted && (
          <>
            {isNotNil(estimatedTime) && <Divider orientation="vertical" />}
            <AgGridStyleTooltip
              label={
                <>
                  <Text>Task Time</Text>
                  <Text c="dimmed" fz="xs">
                    Uses adjusted time first, then logged time, then run time.
                  </Text>
                </>
              }
            >
              <Text span inherit c={isOverEstimate ? 'red' : undefined}>
                {convertSecondsToDuration(taskTime)}
              </Text>
            </AgGridStyleTooltip>
          </>
        )}
      </Group>
    </Badge>
  );
};
